import { Component, Input, Output, EventEmitter, OnDestroy, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { FormGroup, FormBuilder, FormArray, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { distinctValueFilter } from '@app/core/utils';
import { AccessoryFilters, AccessoryFiltersResponse } from '@app/sales/modules/accessories/models/accessory-filters.model';
import { Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { MatCheckbox } from '@angular/material/checkbox';
import { NgClass } from '@angular/common';
import { SpinnerComponent } from '@app/shared/components/atoms/spinner/spinner.component';

class FilterFormGroup {
  isSelected: boolean;
  name: string;
  id: string;
}

export enum MainCategories {
  All = 'Alle produkter',
  PhoneAccessory = 'Mobiltilbehør',
  Tablet = 'Nettbrett',
  SmartWatch = 'Smartklokke',
  MBBAccessory = 'Mobilt Bredbånd',
  Sound = 'Lyd',
}

@Component({
  selector: 'app-accessory-filters',
  templateUrl: './accessory-filters.component.html',
  styleUrls: ['./accessory-filters.component.scss'],
  imports: [SpinnerComponent, FormsModule, ReactiveFormsModule, NgClass, MatCheckbox],
})
export class AccessoryFiltersComponent implements OnInit, OnChanges, OnDestroy {
  private formBuilder = inject(FormBuilder);

  @Input() isLoadingAccessoryFilters: boolean;
  @Input() isLoadingSearchResults: boolean;
  @Input() accessoryFilters: AccessoryFiltersResponse;

  @Output() manufacturerChange = new EventEmitter();
  @Output() subCategoryChange = new EventEmitter();
  @Output() mainCategoryChange = new EventEmitter();
  @Output() onlyDisplayEligibleForAccessoryBinding = new EventEmitter();

  MainCategories = MainCategories;
  filterForm: FormGroup;
  activeMainCategory: MainCategories | string = MainCategories.All;
  activeAccessoryFilters: AccessoryFilters;

  subscriptions$: Subscription[] = [];

  constructor() {
    this.buildForm();
  }

  ngOnInit(): void {
    this.setActiveAccessoryFilters();

    this.initFormArrayFromFilters();
    this.initValueChangeStreams();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.accessoryFilters && !changes.accessoryFilters.previousValue && changes.accessoryFilters.currentValue) {
      this.initFormArrayFromFilters();
      this.initValueChangeStreams();
    }
  }

  onMainCategoryChange(mainCategory: MainCategories | string) {
    this.activeMainCategory = mainCategory;
    this.setActiveAccessoryFilters();
    this.initFormArrayFromFilters();

    this.mainCategoryChange.emit(mainCategory === MainCategories.All ? '' : mainCategory);
  }

  get allManufacturers(): FormGroup[] {
    return (this.filterForm.controls.manufacturers as FormArray).controls as FormGroup[];
  }

  get allMainCategories(): FormGroup[] {
    return (this.filterForm.controls.mainCategories as FormArray).controls as FormGroup[];
  }

  get allSubCategories(): FormGroup[] {
    return (this.filterForm.controls.subCategories as FormArray).controls as FormGroup[];
  }

  isActiveCategory(category: MainCategories | string): boolean {
    return this.activeMainCategory === category;
  }

  private buildForm(): void {
    this.filterForm = this.formBuilder.group({
      manufacturers: new FormArray([]),
      mainCategories: new FormArray([]),
      subCategories: new FormArray([]),
      onlyDisplayEligibleForAccessoryBinding: new FormControl(false),
    });
  }

  private initFormArrayFromFilters(): void {
    if (!this.accessoryFilters) return;

    this.setActiveAccessoryFilters();

    const entries = Object.entries(this.activeAccessoryFilters);

    for (const [key, value] of entries) {
      const currentFormArray = this.filterForm.controls[key] as FormArray;

      if (!currentFormArray) {
        continue;
      }
      currentFormArray.clear();

      value.forEach((formValue: string) => {
        currentFormArray.push(this.createItem(formValue, formValue));
      });
    }
  }

  private setActiveAccessoryFilters(): void {
    if (!this.accessoryFilters) return;
    if (this.activeMainCategory === MainCategories.All) {
      const allSubCategories = this.accessoryFilters.accessoryFilters.map(f => f.subCategories).flat();
      const allManufacturers = this.accessoryFilters.accessoryFilters.map(f => f.manufacturers).flat();

      this.activeAccessoryFilters = {
        mainCategory: MainCategories.All,
        subCategories: allSubCategories.filter(distinctValueFilter),
        manufacturers: allManufacturers.filter(distinctValueFilter),
      } as AccessoryFilters;
    } else {
      this.activeAccessoryFilters = this.accessoryFilters.accessoryFilters.find(
        f => f.mainCategory === this.activeMainCategory
      );
    }
  }

  private createItem(id: string, name: string): FormGroup {
    return new FormGroup({
      id: new FormControl(id),
      name: new FormControl(name),
      isSelected: new FormControl(false),
    });
  }

  private initValueChangeStreams(): void {
    this.subscriptions$.push(
      this.filterForm.controls.onlyDisplayEligibleForAccessoryBinding.valueChanges
        .pipe(tap(onlyDisplayEligible => this.onlyDisplayEligibleForAccessoryBinding.emit(onlyDisplayEligible)))
        .subscribe()
    );

    this.subscriptions$.push(
      this.filterForm.controls.manufacturers.valueChanges
        .pipe(
          tap(newManufacturerControl =>
            this.manufacturerChange.emit(
              newManufacturerControl
                .filter((control: FilterFormGroup) => control.isSelected)
                .map((control: FilterFormGroup) => control.id)
            )
          )
        )
        .subscribe()
    );

    this.subscriptions$.push(
      this.filterForm.controls.subCategories.valueChanges
        .pipe(
          tap(newSubCategoryControl => {
            this.subCategoryChange.emit(
              newSubCategoryControl
                .filter((control: FilterFormGroup) => control.isSelected)
                .map((control: FilterFormGroup) => control.id)
            );
          })
        )
        .subscribe()
    );
  }

  ngOnDestroy(): void {
    this.subscriptions$.forEach(s => s.unsubscribe());
  }
}
